import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, keyframes, CircularProgress, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import {
  Button,
  CommentItem,
  CommentThread,
  IconButton,
  MessageComposer,
  ReactionBar,
} from '@/design-system'
import { AlbumPhoto } from '../../types'
import { useAlbumComments } from '../../hooks/useAlbumComments'
import { getFullImageURL, getPresenceAvatarTone, isValidAvatarUrl } from '@/utils/helper'
import { formatCompactRelative } from '../../utils/date'
import { useTopicViewers } from '@/hooks/common/useTopicViewers'

const AVATAR_TONES = ['gold', 'coral', 'violet', 'cyan'] as const

function toneForUser(id?: string) {
  if (!id) return AVATAR_TONES[0]
  let hash = 0
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return AVATAR_TONES[hash % AVATAR_TONES.length]
}

/** Renders `@Name` mentions in a comment body with an accent color — plain client-side text parsing, no mention/tagging backend. */
function renderWithMentions(text: string) {
  const parts = text.split(/(@[A-Za-z][\w-]*)/g)
  return parts.map((part, i) =>
    part.startsWith('@') ? (
      <Box component='span' key={i} sx={{ color: 'var(--ds-cyan, #01dafe)', fontWeight: 600 }}>
        {part}
      </Box>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  )
}

export interface LightboxProps {
  open: boolean
  photos: AlbumPhoto[]
  index: number
  albumId?: string
  /** Shown in the top bar next to the close button */
  albumTitle?: string
  /** Used to derive the "HOST" chip on comments from the real album owner */
  albumOwnerId?: string
  canDelete?: boolean
  /**
   * Show “Download original”. Default true. When false, the button is hidden
   * and image context-menu / drag-save are soft-blocked client-side.
   */
  allowDownload?: boolean
  /** egoID of the album's current cover photo, if one is set — see albumXData.coverEgoId. */
  coverEgoId?: string
  /** Owner-only "set as cover" action — omit to hide the button entirely. */
  onSetCover?: (photo: AlbumPhoto) => void
  onClose: () => void
  onIndexChange: (index: number) => void
  onToggleLike?: (photo: AlbumPhoto) => void
  onCommentPosted?: (noteId: string) => void
  onDeletePhoto?: (photo: AlbumPhoto) => void
  onShare?: () => void
}

const pop = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.35); }
  100% { transform: scale(1); }
`

const Backdrop = styled(Box)({
  position: 'fixed',
  inset: 0,
  zIndex: 1400,
  backgroundColor: 'var(--ds-immersive)',
  color: 'var(--ds-immersive-ink)',
  display: 'flex',
})

const Top = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '16px 22px',
  background: 'linear-gradient(180deg, rgba(14,13,18,0.7), transparent)',
})

const TopLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  minWidth: 0,
})

const TopRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
})

const TitleBlock = styled(Box)({
  minWidth: 0,
})

const AlbumName = styled(Typography)({
  fontSize: 14.5,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  color: 'var(--ds-night-ink, #f4f3f6)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const MetaLine = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.1em',
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  marginTop: 2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const Stage = styled(Box)({
  flex: 1,
  display: 'flex',
  minWidth: 0,
  minHeight: 0,
  position: 'relative',
})

const StageInner = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  minHeight: 0,
  overflow: 'hidden',
  touchAction: 'none',
  cursor: 'grab',
  padding: '70px 66px 120px',
  boxSizing: 'border-box',
  '&:active': { cursor: 'grabbing' },
})

const Img = styled('img', {
  shouldForwardProp: (p) => p !== '$scale' && p !== '$x' && p !== '$y',
})<{ $scale: number; $x: number; $y: number }>(({ $scale, $x, $y }) => ({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  userSelect: 'none',
  borderRadius: 8,
  boxShadow: '0 40px 110px rgba(0,0,0,0.75)',
  transform: `translate(${$x}px, ${$y}px) scale(${$scale})`,
  transition: $scale === 1 ? 'transform 180ms ease' : 'none',
  willChange: 'transform',
}))

const NavButton = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 8,
  width: 44,
  height: 44,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  background: 'rgba(23,22,29,0.7)',
  border: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
  color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.6))',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  transition:
    'background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': {
    color: 'var(--ds-night-ink, #f4f3f6)',
    background: 'rgba(23,22,29,0.95)',
  },
})

const Dock = styled(Box)({
  position: 'absolute',
  bottom: 78,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 9,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '9px 14px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'rgba(23,22,29,0.8)',
  border: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  boxShadow: 'var(--ds-shadow-night)',
  maxWidth: 'calc(100% - 32px)',
  '@media (max-width: 640px)': {
    '& .kh': { display: 'none' },
  },
})

const KeyHint = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  whiteSpace: 'nowrap',
})

const ReactionWrap = styled(Box, {
  shouldForwardProp: (p) => p !== '$burst',
})<{ $burst?: boolean }>(({ $burst }) => ({
  display: 'inline-flex',
  animation: $burst ? `${pop} 320ms ease` : undefined,
}))

const Strip = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 9,
  display: 'flex',
  gap: 7,
  padding: '12px 22px 16px',
  background: 'linear-gradient(0deg, rgba(14,13,18,0.75), transparent)',
  overflowX: 'auto',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { height: 4 },
})

const FilmThumb = styled('button', {
  shouldForwardProp: (p) => p !== '$active',
})<{ $active?: boolean }>(({ $active }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  width: 46,
  height: 46,
  borderRadius: 8,
  overflow: 'hidden',
  flexShrink: 0,
  cursor: 'pointer',
  opacity: $active ? 1 : 0.4,
  outline: $active ? '2px solid #ffcf7d' : '2px solid transparent',
  outlineOffset: 2,
  transition: 'opacity var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': { opacity: $active ? 1 : 0.8 },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
}))

const CommentsPanel = styled(Box)({
  width: 340,
  maxWidth: '42vw',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  borderLeft: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
  background: 'var(--ds-night-surface, rgba(0,0,0,0.35))',
  '@media (max-width: 900px)': {
    position: 'absolute',
    inset: 'auto 0 0 0',
    width: '100%',
    maxWidth: '100%',
    maxHeight: '48%',
    borderLeft: 'none',
    borderTop: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
  },
})

const RailHead = styled(Box)({
  padding: '18px 20px 14px',
  borderBottom: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
})

const RailTitleRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const RailCount = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
})

const PresenceRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  marginTop: 12,
})

const PresenceAvatar = styled('span')({
  width: 22,
  height: 22,
  borderRadius: '50%',
  border: '2px solid var(--ds-night-surface, #17161d)',
  marginLeft: -8,
  fontSize: 8.5,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:first-of-type': { marginLeft: 0 },
})

const SignInCta = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  textAlign: 'center',
  padding: '16px 20px 18px',
  borderTop: '1px solid var(--ds-night-border, rgba(244,243,246,0.1))',
})

const PresenceLabel = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  letterSpacing: '0.06em',
})

function fullUrl(photo: AlbumPhoto) {
  return photo.url || getFullImageURL(photo.thumbUrl || '')
}

/** Immersive viewer — swipe, zoom, filmstrip, comments, download */
export function Lightbox({
  open,
  photos,
  index,
  albumId,
  albumTitle,
  albumOwnerId,
  canDelete,
  allowDownload = true,
  coverEgoId,
  onSetCover,
  onClose,
  onIndexChange,
  onToggleLike,
  onCommentPosted,
  onDeletePhoto,
  onShare,
}: LightboxProps) {
  const navigate = useNavigate()
  const isDesktop = useMediaQuery('(min-width:981px)')
  const safeIndex = photos.length === 0 ? 0 : Math.min(Math.max(0, index), photos.length - 1)
  const photo = photos[safeIndex]
  const blockSaveGestures = !allowDownload
  const [burst, setBurst] = useState(false)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [draft, setDraft] = useState('')
  const [naturalSize, setNaturalSize] = useState<{
    w: number
    h: number
  } | null>(null)
  const dragRef = useRef<{
    mode: 'pan' | 'swipe' | null
    startX: number
    startY: number
    origX: number
    origY: number
  }>({ mode: null, startX: 0, startY: 0, origX: 0, origY: 0 })
  const filmRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { comments, loading, posting, canComment, post } = useAlbumComments(
    albumId,
    commentsOpen ? photo?.noteId : undefined,
  )

  const resetZoom = useCallback(() => {
    setScale(1)
    setOffset({ x: 0, y: 0 })
  }, [])

  const goPrev = useCallback(() => {
    if (photos.length <= 1) return
    onIndexChange((safeIndex - 1 + photos.length) % photos.length)
  }, [onIndexChange, photos.length, safeIndex])

  const goNext = useCallback(() => {
    if (photos.length <= 1) return
    onIndexChange((safeIndex + 1) % photos.length)
  }, [onIndexChange, photos.length, safeIndex])

  useEffect(() => {
    resetZoom()
    setNaturalSize(null)
  }, [safeIndex, resetZoom])

  // Open the comments rail by default on desktop, matching the always-visible
  // rail in the mockup; mobile stays closed until the viewer taps to open it.
  useEffect(() => {
    if (open) setCommentsOpen(isDesktop)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on open, not every resize
  }, [open])

  // Preload neighbors
  useEffect(() => {
    if (!open) return
    ;[safeIndex - 1, safeIndex + 1].forEach((i) => {
      const wrapped = (i + photos.length) % photos.length
      if (wrapped === safeIndex || photos.length <= 1) return
      const img = new Image()
      img.src = fullUrl(photos[wrapped])
    })
  }, [open, safeIndex, photos])

  // Lock body scroll
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setDraft('')
      resetZoom()
    }
  }, [open, resetZoom])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      // Don't steal keys while typing a comment (or any other field) —
      // `C` toggles the notes rail and was closing it mid-sentence.
      const target = e.target as HTMLElement | null
      const typing =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      if (e.key === 'Escape') {
        if (typing) {
          ;(target as HTMLElement).blur()
          return
        }
        if (commentsOpen && !isDesktop) setCommentsOpen(false)
        else onClose()
        return
      }
      if (typing) return
      if (scale > 1) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
      }
      if ((e.key === 'l' || e.key === 'L') && photo && onToggleLike) {
        onToggleLike(photo)
        setBurst(true)
      }
      if (e.key === 'c' || e.key === 'C') setCommentsOpen((v) => !v)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, goPrev, goNext, onClose, photo, onToggleLike, commentsOpen, scale, isDesktop])

  useEffect(() => {
    if (!burst) return
    const t = window.setTimeout(() => setBurst(false), 340)
    return () => window.clearTimeout(t)
  }, [burst])

  useEffect(() => {
    if (!listRef.current) return
    listRef.current.scrollTop = listRef.current.scrollHeight
  }, [comments.length])

  // Keep active filmstrip thumb in view
  useEffect(() => {
    const el = filmRef.current?.children[safeIndex] as HTMLElement | undefined
    el?.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [safeIndex])

  const like = () => {
    if (!photo) return
    onToggleLike?.(photo)
    setBurst(true)
  }

  const download = async () => {
    if (!photo || !allowDownload) return
    const url = fullUrl(photo)
    try {
      const res = await fetch(url)
      const blob = await res.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `egowall-${photo.egoId || safeIndex + 1}.jpg`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch {
      window.open(url, '_blank')
    }
  }

  const blockContextMenu = (e: React.MouseEvent) => {
    if (!blockSaveGestures) return
    e.preventDefault()
    e.stopPropagation()
  }

  const onPointerDown = (e: React.PointerEvent) => {
    // Don't steal clicks from nav / action buttons inside the stage
    if ((e.target as HTMLElement).closest('button, a, input, textarea')) return
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    dragRef.current = {
      mode: scale > 1 ? 'pan' : 'swipe',
      startX: e.clientX,
      startY: e.clientY,
      origX: offset.x,
      origY: offset.y,
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d.mode) return
    const dx = e.clientX - d.startX
    if (d.mode === 'pan') {
      setOffset({ x: d.origX + dx, y: d.origY + (e.clientY - d.startY) })
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d.mode) return
    const dx = e.clientX - d.startX
    if (d.mode === 'swipe' && Math.abs(dx) > 60 && scale === 1) {
      if (dx < 0) goNext()
      else goPrev()
    }
    dragRef.current.mode = null
  }

  const stageRef = useRef<HTMLDivElement>(null)

  // Non-passive wheel for pinch-zoom style scroll
  useEffect(() => {
    const el = stageRef.current
    if (!el || !open) return
    const onWheelNative = (e: WheelEvent) => {
      if (!e.ctrlKey && Math.abs(e.deltaY) < 40) return
      e.preventDefault()
      setScale((s) => {
        const next = Math.min(4, Math.max(1, s - e.deltaY * 0.002))
        if (next === 1) setOffset({ x: 0, y: 0 })
        return next
      })
    }
    el.addEventListener('wheel', onWheelNative, { passive: false })
    return () => el.removeEventListener('wheel', onWheelNative)
  }, [open, safeIndex])

  const onWheel = (_e: React.WheelEvent) => {
    /* handled natively for preventDefault */
  }

  const toggleZoom = () => {
    if (scale > 1) resetZoom()
    else setScale(2)
  }

  const submitComment = async () => {
    if (!photo?.noteId || !draft.trim()) return
    const result = await post(draft)
    if (result) {
      setDraft('')
      onCommentPosted?.(photo.noteId)
    }
  }

  // Real "who's viewed this gallery/album recently" presence — driven by the
  // live topic_view socket event (see useTopicViewers). Topic-scoped, not
  // photo-scoped, and only ever populated for the owner (the backend never
  // notifies anyone else about a view).
  const recentViewers = useTopicViewers(albumId)

  if (!open || !photo) return null

  const metaParts = [`${safeIndex + 1} / ${photos.length}`]
  if (naturalSize) metaParts.push(`${naturalSize.w}×${naturalSize.h} ORIGINAL`)
  // On mobile the comments panel becomes a bottom sheet over the stage — hide
  // the dock/filmstrip while it's open so they don't compete for the same space.
  const hideStageChrome = commentsOpen && !isDesktop

  return (
    <Backdrop role='dialog' aria-modal>
      <Stage>
        <Top>
          <TopLeft>
            <Button
              variant='night'
              size='sm'
              onClick={onClose}
              sx={{ px: '10px !important', minWidth: 0 }}
            >
              ✕
            </Button>
            {albumTitle && (
              <TitleBlock>
                <AlbumName>{albumTitle}</AlbumName>
                <MetaLine>{metaParts.join(' · ')}</MetaLine>
              </TitleBlock>
            )}
          </TopLeft>
          <TopRight>
            {allowDownload && (
              <Button variant='night' size='sm' onClick={download}>
                ⤓ Download original
              </Button>
            )}
            {onShare && (
              <Button variant='night' size='sm' onClick={onShare}>
                ⚿ Share
              </Button>
            )}
            <IconButton tone='night' onClick={toggleZoom} aria-label='Zoom'>
              {scale > 1 ? <ZoomOutIcon fontSize='small' /> : <ZoomInIcon fontSize='small' />}
            </IconButton>
            {onSetCover && photo.egoId && (
              <IconButton
                tone='night'
                onClick={() => onSetCover(photo)}
                aria-label={photo.egoId === coverEgoId ? 'Album cover' : 'Set as album cover'}
                sx={{
                  color: photo.egoId === coverEgoId ? '#ffcf7d !important' : undefined,
                }}
              >
                {photo.egoId === coverEgoId ? (
                  <StarIcon fontSize='small' />
                ) : (
                  <StarBorderIcon fontSize='small' />
                )}
              </IconButton>
            )}
            {canDelete && onDeletePhoto && (
              <IconButton
                tone='night'
                onClick={() => onDeletePhoto(photo)}
                aria-label='Delete photo'
              >
                <DeleteOutlineIcon fontSize='small' />
              </IconButton>
            )}
            <IconButton
              tone='night'
              onClick={() => setCommentsOpen((v) => !v)}
              sx={{
                color: commentsOpen ? 'var(--ds-night-ink, #f4f3f6) !important' : undefined,
              }}
              aria-label='Toggle comments'
            >
              <ChatBubbleOutlineIcon fontSize='small' />
            </IconButton>
          </TopRight>
        </Top>

        {photos.length > 1 && scale === 1 && (
          <NavButton
            type='button'
            onClick={(e) => {
              e.stopPropagation()
              goPrev()
            }}
            style={{ left: 16 }}
            aria-label='Previous'
          >
            <ChevronLeftIcon />
          </NavButton>
        )}
        <StageInner
          ref={stageRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
          onDoubleClick={like}
          onContextMenu={blockContextMenu}
        >
          <Img
            src={fullUrl(photo)}
            alt=''
            draggable={false}
            $scale={scale}
            $x={offset.x}
            $y={offset.y}
            onContextMenu={blockContextMenu}
            onDragStart={(e) => {
              if (blockSaveGestures) e.preventDefault()
            }}
            style={blockSaveGestures ? { WebkitTouchCallout: 'none', userSelect: 'none' } : undefined}
            onLoad={(e) => {
              const img = e.currentTarget
              setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight })
            }}
          />
        </StageInner>
        {photos.length > 1 && scale === 1 && (
          <NavButton
            type='button'
            onClick={(e) => {
              e.stopPropagation()
              goNext()
            }}
            style={{ right: 16 }}
            aria-label='Next'
          >
            <ChevronRightIcon />
          </NavButton>
        )}

        {onToggleLike && scale === 1 && !hideStageChrome && (
          <Dock>
            <ReactionWrap $burst={burst}>
              <ReactionBar
                tone='glass-dark'
                reactions={[
                  {
                    id: 'love',
                    emoji: '❤',
                    count: photo.likeCount || 0,
                    emphasis: true,
                  },
                ]}
                activeIds={photo.liked ? ['love'] : []}
                onToggle={like}
              />
            </ReactionWrap>
            <KeyHint className='kh'>⌨ ← → BROWSE · L LOVE · ESC BACK</KeyHint>
          </Dock>
        )}

        {photos.length > 1 && !hideStageChrome && (
          <Strip ref={filmRef}>
            {photos.map((p, i) => (
              <FilmThumb
                key={p.id}
                type='button'
                $active={i === safeIndex}
                onClick={() => onIndexChange(i)}
                onContextMenu={blockContextMenu}
              >
                <img src={p.thumbUrl || p.url} alt='' draggable={false} onContextMenu={blockContextMenu} />
              </FilmThumb>
            ))}
          </Strip>
        )}
      </Stage>

      {commentsOpen && (
        <CommentsPanel onClick={(e) => e.stopPropagation()}>
          <RailHead>
            <RailTitleRow>
              <Typography sx={{ fontSize: 14, fontWeight: 680 }}>Comments on this photo</Typography>
              <RailCount>
                {comments.length} {comments.length === 1 ? 'NOTE' : 'NOTES'}
              </RailCount>
            </RailTitleRow>
            {recentViewers.length > 0 && (
              <PresenceRow>
                {recentViewers.slice(0, 3).map((v) => {
                  const hasPhoto = isValidAvatarUrl(v.profilePicThumbURL || '')
                  const tone = getPresenceAvatarTone(v.sourceUserID)
                  return (
                    <PresenceAvatar
                      key={v.sourceUserID}
                      style={
                        hasPhoto
                          ? { backgroundImage: `url(${v.profilePicThumbURL})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                          : { background: tone.bg, color: tone.ink }
                      }
                    >
                      {!hasPhoto && v.sourceUserFullName?.[0]?.toUpperCase()}
                    </PresenceAvatar>
                  )
                })}
                <PresenceLabel>
                  {recentViewers.length} VIEWED RECENTLY
                </PresenceLabel>
              </PresenceRow>
            )}
          </RailHead>
          <CommentThread ref={listRef} sx={{ px: 2.5, py: 2 }}>
            {loading && (
              <Box display='flex' justifyContent='center' py={3}>
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              </Box>
            )}
            {!loading && comments.length === 0 && (
              <Typography sx={{ fontSize: 13, opacity: 0.55, textAlign: 'center', py: 3 }}>
                Be the first to leave a note
              </Typography>
            )}
            {comments.map((c) => (
              <CommentItem
                key={c.feedbackID}
                avatarLabel={(c.senderName || '?')[0]}
                avatarTone={toneForUser(c.senderID || c.userID)}
                name={c.senderName}
                isHost={
                  Boolean(albumOwnerId) && String(c.senderID || c.userID) === String(albumOwnerId)
                }
                timestamp={formatCompactRelative(c.dateAdded)}
              >
                {renderWithMentions(c.comment)}
              </CommentItem>
            ))}
          </CommentThread>
          {canComment ? (
            <MessageComposer
              value={draft}
              onChange={setDraft}
              onSubmit={submitComment}
              placeholder='Say something about this moment…'
              hintRight='ENTER SENDS'
              disabled={posting}
            />
          ) : (
            <SignInCta>
              <Typography sx={{ fontSize: 12.5, color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.6))' }}>
                Sign up free to join the conversation
              </Typography>
              <Button variant='spectrum' size='sm' onClick={() => navigate('/signup')}>
                Sign up — free <span aria-hidden>→</span>
              </Button>
            </SignInCta>
          )}
        </CommentsPanel>
      )}
    </Backdrop>
  )
}
